import { Box, Typography, Stack, Alert } from "@mui/material";
import { useState } from "react";
import { Spinner, Button } from "react-bootstrap";

import {
  useAddNoteMutation,
  useUpdateNoteMutation,
} from "api/memberPortal/notes/notes.api";
import { useAppForm } from "components/Forms/AppForm";
import DialogModal from "components/Forms/components/modals/DialogModal";
import { useAuth } from "hooks/useAuth";
import { getISOCurrentDate } from "utils/timeUtils";

import {
  PPNoteFormProps,
  InternalOnlyNoteFormValues,
  InternalOnlyNoteFormSchema,
} from "./NoteForm.schema";

const PPNoteForm = ({
  show,
  handleClose,
  note,
  protectionProfile,
  replyTo,
}: PPNoteFormProps) => {
  const { currentUser, permissions } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [triggerUpdateNote] = useUpdateNoteMutation();
  const [triggerAddNote] = useAddNoteMutation();

  const isNiap = permissions.role_type === "NIAP";
  const isValidator = permissions.role_type === "Validator";

  const isNiapOrValidator = isNiap || isValidator;

  const ppNoteForm = useAppForm({
    defaultValues: {
      internal_only: note?.internal_only ?? false,
      title: note?.title,
      note: note?.note,
    } as InternalOnlyNoteFormValues,
    validators: {
      onSubmit: InternalOnlyNoteFormSchema,
    },
    onSubmit: async ({ value }) => {
      setError(null);
      setLoading(true);
      try {
        if (note?.note_id) {
          await triggerUpdateNote({
            noteId: note.note_id,
            body: {
              ...value,
            },
          });
        } else {
          await triggerAddNote({
            ...value,
            note_type: "protection_profile",
            note_type_id: protectionProfile.pp_id,
            visible_to_smes: false,
            visible_to_labs: false,
            visible_to_validators: false,
            in_response_to: replyTo?.note_id,
          });
        }
        handleClose();
        setLoading(false);
      } catch (error) {
        console.error(error);
        setError("Error adding note. Please try again.");
        setLoading(false);
      }
    },
  });

  return (
    <DialogModal
      title={`+ NOTE for Protection Profile ${protectionProfile.pp_id}`}
      open={show}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          timeout: 300,
        },
      }}
    >
      <Box>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            ppNoteForm.handleSubmit();
          }}
        >
          {/* WARN: Custom styling from pre standardization! */}
          {/* TODO: Standardize when you come across this!  */}
          <Typography id="modal-modal-description" variant="body1">
            <Typography variant="body1" fontWeight="bold" className="mb-3">
              Submitter: {currentUser.username}
            </Typography>
            <Box className="mb-3">
              <Stack spacing={1}>
                {isNiapOrValidator && (
                  <ppNoteForm.AppField name="internal_only">
                    {(field) => (
                      <field.YesNoRadioButton
                        label="Is Private?"
                        isHorizontal={true}
                      />
                    )}
                  </ppNoteForm.AppField>
                )}
              </Stack>
            </Box>
            <Stack spacing={3}>
              <ppNoteForm.AppField name="title">
                {(field) => (
                  <field.TextInput label="Title" data-cy="note-title" />
                )}
              </ppNoteForm.AppField>

              <ppNoteForm.AppField name="note">
                {(field) => <field.HtmlEditor label="Overview" />}
              </ppNoteForm.AppField>
            </Stack>
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              p: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button variant="secondary" onClick={() => handleClose()}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={loading}
              type={loading ? "button" : "submit"}
              data-cy="submit-note"
            >
              {loading && <Spinner animation="border" size="sm" />}
              {note?.note_id ? "Update" : "Add"}
            </Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
              {error}
            </Alert>
          )}
        </form>
      </Box>
    </DialogModal>
  );
};

export default PPNoteForm;
